import { supabase } from './supabase';

/*
  How long OnHand keeps things, and the removal of what is past it.

  Job 17, part three, 7 September 2026. The owner's decision: "after 3 years the
  data is removed". The rule and the removal both live in the database, in
  `retention_years()`, `retention_due()` and `retention_run()`, because two of
  the tables involved have no delete rule that would let the browser touch them
  and a delete the database refuses reports success. The migration says more.

  **It also runs on its own, every night**, for every site, by the owner's
  decision of 7 September 2026 (*"Yes run the 3 year data dump on a schedule"*,
  `20260907120000_job_17_the_removal_on_a_schedule.sql`). The screen shows the
  rule, shows exactly what is over the line right now, counted rather than
  guessed, and the button removes it now rather than tonight. **This comment
  said nothing ran on its own** until 19 September 2026, and so did the screen,
  for twelve days after the schedule went live (the audit after the rebrand,
  issue 24).
*/

export const DEFAULT_YEARS = 3;

/*
  **The three-year rule is about records of things that happened.** A count
  cycle that closed, a prior count, an order run, a client order, an alert, a
  task, a line in the audit log. Those have a date on them and after three years
  they go.

  **It is not about what the business currently is.** The item catalog, the
  locations, the people, the regions, the accounts, the vendor list and the
  site's own settings stay, however old the rows are. A location added four
  years ago is still the location.
*/
export const KEPT = [
  'The item catalog',
  'Locations, regions and accounts',
  'People, and who can see what',
  'The vendor list',
  "This site's own settings, terms and release notes",
];

export const REMOVED = [
  'Closed count cycles, and every sheet and line under them',
  'Prior counts loaded from earlier years',
  'Order runs and client orders',
  'Alerts and tasks',
  'The audit log',
  'Regional Product Movement runs',
];

export async function loadYears() {
  const { data, error } = await supabase
    .from('app_settings').select('value').eq('key', 'data_retention_years').maybeSingle();
  if (error) return { years: DEFAULT_YEARS, error: error.message };
  const n = parseInt(String(data?.value ?? ''), 10);
  return { years: Number.isFinite(n) && n >= 1 ? n : DEFAULT_YEARS, error: null };
}

/*
  Saving is proved by reading the row back, never by the absence of an error.
  A write no rule permits changes nothing, raises nothing and answers "fine".
*/
export async function saveYears(years) {
  const n = parseInt(String(years), 10);
  if (!Number.isFinite(n) || n < 1) return { ok: false, error: 'A number of years, at least 1.' };
  const { error } = await supabase.from('app_settings')
    .upsert({ key: 'data_retention_years', value: String(n) }, { onConflict: 'org_id,key' });
  if (error) return { ok: false, error: error.message };
  const back = await loadYears();
  if (back.years !== n) return { ok: false, error: 'The setting did not save. It still reads ' + back.years + '.' };
  return { ok: true, years: n, error: null };
}

export async function loadDue() {
  const { data, error } = await supabase.rpc('retention_due');
  if (error) return { rows: null, error: error.message };
  return { rows: (data || []).slice().sort((a, b) => a.ord - b.ord), error: null };
}

export async function runRemoval() {
  const { data, error } = await supabase.rpc('retention_run');
  if (error) return { rows: null, error: error.message };
  return { rows: (data || []).slice().sort((a, b) => a.ord - b.ord), error: null };
}

export function totalDue(rows) {
  return (rows || []).reduce((n, r) => n + Number(r.n || 0), 0);
}

/*
  What the screen says above the list.

  **Zero gets a sentence of its own** rather than an empty list, because an empty
  list and "nothing is old enough yet" look the same and mean different things.
  Every site says zero today: the oldest row in this database is from 20 August
  2026, so nothing reaches three years until August 2029.
*/
export function dueSentence(rows, years) {
  const total = totalDue(rows);
  const y = years || DEFAULT_YEARS;
  if (total === 0) return 'Nothing in this site is more than ' + y + ' years old, so nothing would be removed.';
  const kinds = (rows || []).filter(r => Number(r.n) > 0).length;
  return total.toLocaleString() + ' ' + (total === 1 ? 'row' : 'rows')
       + ' across ' + kinds + ' ' + (kinds === 1 ? 'kind' : 'kinds')
       + ' are more than ' + y + ' years old.';
}

export function removedSentence(rows) {
  const total = totalDue(rows);
  if (total === 0) return 'Nothing was old enough to remove.';
  return 'Removed ' + total.toLocaleString() + ' ' + (total === 1 ? 'row' : 'rows') + '.';
}
